
import { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Music } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { fadeInVariants } from '@/lib/animations';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ProfileForm from '@/components/profile/ProfileForm';
import PasswordChangeForm from '@/components/profile/PasswordChangeForm';
import { useAuth } from '@/context/AuthContext';
import { AuthMigrationDebug } from '@/components/debug/AuthMigrationDebug';

interface CompletedMeditation {
  id: string;
  meditation_id: string;
  completed_at: string;
}

interface ProfileData {
  full_name?: string;
  email?: string;
  bio?: string;
  job_title?: string;
  company?: string;
  whatsapp_number?: string;
}

const Profile = () => {
  const [completedMeditations, setCompletedMeditations] = useState<CompletedMeditation[]>([]);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Get completed meditations
          const { data: meditations, error: meditationsError } = await supabase
            .from('completed_meditations')
            .select('*')
            .eq('user_id', session.user.id)
            .order('completed_at', { ascending: false });
          
          if (meditationsError) {
            console.error('Error fetching meditations:', meditationsError);
          } else {
            setCompletedMeditations(meditations || []);
          }

          // Fetch profile data
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
          } else {
            setProfileData(profile || {});
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, []);

  const insightsProgress = Math.min(completedMeditations.length / 3, 1) * 100;

  return (
    <MainLayout>
      <div className="container mx-auto p-6">
        <h1 className="text-2xl font-bold mb-8">visão geral</h1>
        <p className="text-sm text-gray-500 mb-8">suas notas mentais - sim, nós as guardamos</p>
        
        {/* Debug Component - only for development */}
        <div className="mb-8">
          <AuthMigrationDebug />
        </div>
        
        {/* Profile Form Section */}
        <div className="mb-10">
          <h2 className="text-lg font-medium mb-4">informações pessoais</h2>
          <ProfileForm initialData={profileData || undefined} />
        </div>

        {/* Password Change Section */}
        <div className="mb-10">
          <h2 className="text-lg font-medium mb-4">alterar senha</h2>
          <PasswordChangeForm />
        </div>
        
        <div className="mb-10">
          <h2 className="flex items-center text-lg font-medium mb-2">
            <span className="mr-2">📝</span> citação do dia
          </h2>
          <p className="text-gray-700 italic">
            "A chave para o seu próximo passo está em confiar no timing do seu coração."
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium mb-2">meditações concluídas</h3>
            <p className="text-3xl font-bold">{completedMeditations.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium mb-2">tempo total meditando</h3>
            <p className="text-3xl font-bold">{completedMeditations.length * 10}m</p>
          </div>
        </div>
        
        <div className="mb-10">
          <h2 className="text-lg font-medium mb-4">
            Vive Calme está te conhecendo!
          </h2>
          <p className="text-sm text-gray-600 mb-2">
            complete mais algumas sessões significativas para desbloquear seus insights.
          </p>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-brand-blue rounded-full" style={{ width: `${insightsProgress}%` }}></div>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">
            {completedMeditations.length} / 3
          </p>
        </div>
        
        {completedMeditations.length > 0 && (
          <motion.div
            className="mb-10"
            variants={fadeInVariants}
            initial="initial"
            animate="animate"
          >
            <h2 className="text-lg font-medium mb-4">meditações recentes</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {completedMeditations.slice(0, 4).map((meditation) => (
                <Card key={meditation.id} className="overflow-hidden">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Music className="text-brand-blue h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">Meditação Concluída</h4>
                      <p className="text-sm text-gray-500">
                        {new Date(meditation.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className="ml-auto">{meditation.meditation_id.split('-')[0]}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
        
        <div className="bg-brand-blue text-white p-6 rounded-lg mb-8">
          <h2 className="text-lg font-medium mb-4">iniciar nova sessão</h2>
          <Link to="/meditacoes">
            <Button className="bg-white text-brand-blue font-medium px-4 py-2 rounded hover:bg-opacity-90 transition-colors">
              explorar meditações ➔
            </Button>
          </Link>
        </div>
      </div>
    </MainLayout>
  );
};

export default Profile;
